import type Phaser from 'phaser';
import { MOWER_IDS } from '../../content/mowers';
import { createFxTexture, FX_KEY } from '../fx/Effects';
import {
  createCritters,
  createMower,
  createPickups,
  createRival,
  createTiles,
  CRITTERS_KEY,
  RIVAL_KEY,
  mowerTextureKey,
  PICKUPS_KEY,
  TILES_KEY,
} from '../render/placeholderArt';
import { createItems, ITEM_FRAMES, ITEMS_KEY } from '../render/itemArt';
import { mowerFrameSize } from '../render/mowerArt';
import { TILE_FRAMES } from '../render/tileFrames';
import {
  createCrowns,
  createGrandCrowns,
  CROWN_FRAMES,
  CROWN_SIZE,
  CROWNS_KEY,
  GRAND_CROWN_FRAMES,
  GRAND_CROWN_SIZE,
  GRAND_CROWNS_KEY,
} from '../render/treeArt';
import { BLADE_FRAMES, BLADE_H, BLADE_W, BLADES_KEY, createBlades } from '../render/bladesArt';
import { createProps, PROP_FRAMES, PROP_H, PROP_W, PROPS_KEY } from '../render/props';

/**
 * Every texture sheet the game draws from: the contract an artist (or a purchased pack) fills in.
 * Each sheet is a single row of equally sized frames, in the order listed. A sheet listed in
 * public/art/manifest.json loads from that PNG; anything else uses the generated placeholder.
 * Templates: `pnpm art:export` writes every sheet to docs/art/templates/.
 */
export interface ArtSheet {
  key: string;
  frameWidth: number;
  frameHeight: number;
  /** Frame names in order (documentation and validation). */
  frames: readonly string[];
  generate: (scene: Phaser.Scene) => void;
}

const MOWER_FRAMES = ['north', 'east', 'south', 'west', 'broken'] as const;

export const ART_SHEETS: readonly ArtSheet[] = [
  { key: TILES_KEY, frameWidth: 16, frameHeight: 16, frames: TILE_FRAMES, generate: createTiles },
  ...MOWER_IDS.map((id): ArtSheet => ({
    key: mowerTextureKey(id),
    // Bigger than a tile so riding mowers can overhang it; centered on the tile.
    frameWidth: mowerFrameSize(id),
    frameHeight: mowerFrameSize(id),
    frames: MOWER_FRAMES,
    generate: (scene) => createMower(scene, id),
  })),
  {
    key: PICKUPS_KEY,
    frameWidth: 16,
    frameHeight: 16,
    frames: ['coin', 'gas', 'repair', 'rare', 'hoard', 'turbo', 'trim'],
    generate: createPickups,
  },
  {
    key: ITEMS_KEY,
    frameWidth: 16,
    frameHeight: 16,
    frames: ITEM_FRAMES,
    generate: createItems,
  },
  {
    key: CRITTERS_KEY,
    frameWidth: 16,
    frameHeight: 16,
    frames: ['squirrel', 'rabbit', 'dog', 'sheep', 'mole', 'deer', 'fox', 'stag'],
    generate: createCritters,
  },
  {
    key: RIVAL_KEY,
    frameWidth: 16,
    frameHeight: 16,
    frames: ['north', 'east', 'south', 'west'],
    generate: createRival,
  },
  {
    key: PROPS_KEY,
    frameWidth: PROP_W,
    frameHeight: PROP_H,
    frames: PROP_FRAMES,
    generate: createProps,
  },
  {
    key: BLADES_KEY,
    frameWidth: BLADE_W,
    frameHeight: BLADE_H,
    frames: BLADE_FRAMES,
    generate: createBlades,
  },
  {
    key: GRAND_CROWNS_KEY,
    frameWidth: GRAND_CROWN_SIZE,
    frameHeight: GRAND_CROWN_SIZE,
    frames: GRAND_CROWN_FRAMES,
    generate: createGrandCrowns,
  },
  {
    key: CROWNS_KEY,
    frameWidth: CROWN_SIZE,
    frameHeight: CROWN_SIZE,
    frames: CROWN_FRAMES,
    generate: createCrowns,
  },
  {
    key: FX_KEY,
    frameWidth: 4,
    frameHeight: 4,
    frames: [
      'clipLight',
      'clipDark',
      'sparkle',
      'dust',
      'partLight',
      'partDark',
      'petal',
      'bladeLight',
      'bladeDark',
      'bee',
    ],
    generate: createFxTexture,
  },
];

export const ART_MANIFEST_KEY = 'art-manifest';
export const ART_MANIFEST_PATH = 'art/manifest.json';

export interface ArtManifest {
  /** Sheet key → PNG path (relative to the site root), e.g. "tiles": "art/tiles.png". */
  sheets?: Record<string, string>;
}

/** Queues PNG overrides from the manifest; returns how many were queued. */
export function queueArtOverrides(scene: Phaser.Scene, manifest: ArtManifest | undefined): number {
  let queued = 0;
  for (const sheet of ART_SHEETS) {
    const path = manifest?.sheets?.[sheet.key];
    if (!path) continue;
    scene.load.spritesheet(sheet.key, path, {
      frameWidth: sheet.frameWidth,
      frameHeight: sheet.frameHeight,
    });
    queued++;
  }
  return queued;
}

/**
 * Makes sure every sheet exists: loaded overrides are kept if they have enough frames (otherwise
 * dropped with a warning), and anything missing is generated.
 */
export function ensureArt(scene: Phaser.Scene): void {
  for (const sheet of ART_SHEETS) {
    if (scene.textures.exists(sheet.key)) {
      // frameTotal includes the implicit __BASE frame.
      const frames = scene.textures.get(sheet.key).frameTotal - 1;
      if (frames >= sheet.frames.length) continue;
      console.warn(
        `[art] ${sheet.key}: expected ${sheet.frames.length} frames of ${sheet.frameWidth}x${sheet.frameHeight}, ` +
          `got ${frames}; using the placeholder`,
      );
      scene.textures.remove(sheet.key);
    }
    sheet.generate(scene);
  }
}
